import { v4 as uuidv4 } from 'uuid';
import { UserResponseProps } from '../types';

export type PurchaseItemsProps = {
	productId: string;
	quantity: number;
	sellingPrice: number | null;
	discount: number | null;
	unitCost: number | null;
	totalAmount: number;
	variantId?: string | null;
};

export type TransactionTypes = 'stockOut' | 'stockIn';

export type TransactionStatuses = 'purchase' | 'return' | 'adjustment' | 'sales' | 'transfer' | 'setup';

/**
 * Deterministic inventory doc id for a sale stockOut line.
 *
 * CROSS-REPO CONTRACT: the backend processSales composes the IDENTICAL id (same
 * helper name/semantics in shoppa-backend src/utils/inventoryLog.ts) for the same
 * sale line, so a second composition (client retry, backend re-process) collides
 * on _id instead of silently duplicating the stock-out. Do not change this formula
 * without changing the backend in lockstep.
 *
 * Shape: `inventory-${saleDocId}-${baseProductId}` plus `-${variant}` when the sale
 * line carries a variant. Variant sale lines carry a COLON-JOINED productId
 * (`product-xxx:variantId`, see flattenedProductData in composeProducts.ts), so the
 * base product id is normalized via split(':') and the variant component falls back
 * to the colon-embedded suffix when no explicit variantId is present.
 *
 * Safe without a line index: the cart merges lines by raw productId equality
 * (handleUpdateCart) and variants flatten to distinct colon ids, so
 * (sale, product, variant) is unique per sale.
 */
export const composeSaleInventoryId = ({
	saleDocId,
	productId,
	variantId,
}: {
	saleDocId: string;
	productId: string;
	variantId?: string | null;
}) => {
	const [baseProductId, embeddedVariantId] = productId.split(':');
	const variant = variantId || embeddedVariantId || null;
	return variant ? `inventory-${saleDocId}-${baseProductId}-${variant}` : `inventory-${saleDocId}-${baseProductId}`;
};

type BulkWriteEntryShape = { error?: unknown; name?: unknown; status?: unknown; id?: unknown };

/**
 * Classifies PouchDB bulkDocs results into benign idempotency conflicts vs real
 * per-doc errors. A 409 `conflict` (PouchDB bulkDocs conflict entries carry
 * `name: 'conflict'` / `status: 409`) on a deterministic sale-inventory id means
 * the doc already exists — the idempotency working as designed. Callers log
 * conflicts as info (never Sentry errors), report only `errors` as exceptions,
 * and continue in both cases — a partial bulk failure never fails the sale.
 */
export const classifyBulkWriteFailures = <T extends object>(results: Array<T | Error>) => {
	const failures = results.filter(
		(r) => r instanceof Error || ('error' in r && Boolean((r as BulkWriteEntryShape).error))
	);
	const isConflict = (r: T | Error) => {
		const entry = r as BulkWriteEntryShape;
		return entry.name === 'conflict' || entry.status === 409;
	};
	return {
		conflicts: failures.filter(isConflict),
		errors: failures.filter((r) => !isConflict(r)),
	};
};

export const composeSingleInventoryLog = ({
	purchaseItem,
	transactionType,
	referenceTransactionId,
	referenceTransactionNo,
	transactionStatus,
	variantId,
	userInfo,
	note,
	reason,
	productName,
	refCartId,
	adjustmentDate,
	images,
	externalReferenceNumber,
	assignedLocation,
	fromLocation,
	toLocation,
	sourceCreatedDate,
	userFullName,
	userIdentity,
	referenceInvoiceId,
	customer,
	overrideId,
}: {
	purchaseItem: PurchaseItemsProps;
	transactionType: TransactionTypes;
	transactionStatus: TransactionStatuses;
	referenceTransactionId: string | null;
	referenceTransactionNo: string | null;
	variantId?: string;
	userInfo: UserResponseProps | null;
	note?: string | null;
	reason?: string | null;
	assignedLocation?: UserResponseProps['assignedLocations'][0];
	productName: string | null;
	refCartId: string | null;
	adjustmentDate?: string | null;
	images?: string[] | null;
	externalReferenceNumber?: string | null;
	// processed?: boolean;
	fromLocation?: UserResponseProps['assignedLocations'][0];
	toLocation?: UserResponseProps['assignedLocations'][0];
	sourceCreatedDate?: string;
	userFullName?: string;
	userIdentity?: string;
	referenceInvoiceId?: string;
	customer?: any;
	/** Deterministic _id (see composeSaleInventoryLogId). Default: random uuid. */
	overrideId?: string;
}) => {
	const { profile, assignedLocations, _id: userId, merchantName } = userInfo ?? {};

	const createdByUser = {
		id: userIdentity || userId || null,
		name: userFullName
			? userFullName
			: profile?.lastName && profile?.firstName
				? `${profile?.lastName} ${profile?.firstName}`
				: null,
	};

	const { productId, quantity, unitCost, variantId: innervariantId, sellingPrice, discount } = purchaseItem;

	const creationDate = new Date().toISOString();

	const location = assignedLocations?.[0];

	return {
		_id: overrideId ?? `inventory-${uuidv4()}`,
		productId,
		referenceTransactionId: referenceTransactionId || null,
		referenceTransactionNo: referenceTransactionNo || null,
		type: 'inventory',
		transactionType: transactionType || null,
		transactionStatus: transactionStatus || null,
		quantity: quantity || null,
		isVoid: false,
		assignedLocation: assignedLocation || location || null,
		referenceCartId: refCartId || null,
		totalAmount: quantity * (sellingPrice || 0) || null,
		sellingPrice: sellingPrice || null,
		discount: discount || null,
		productName: productName || null,
		purchasePrice: unitCost || null,
		merchantName,
		variantId: innervariantId || variantId || null,
		createdAt: sourceCreatedDate || creationDate,
		updatedAt: sourceCreatedDate || creationDate,
		fromLocation: fromLocation || null,
		toLocation: toLocation || null,
		// processed,
		createdBy: createdByUser,
		editedBy: null,
		note: note || null,
		reason: reason || null,
		adjustmentDate: adjustmentDate || null,
		images: (images || []).length > 0 ? images : null,
		externalReferenceNumber: externalReferenceNumber || null,
		referenceInvoiceId: referenceInvoiceId || null,
		customer: customer || null,
	};
};
